import React, { useEffect, useImperativeHandle, type JSX } from 'react';

import { useConfirm } from '@fiji/common/src/hooks';
import { CustomModal, CustomTransComponent } from '../../../components';
import { FormControlLabel, FormHelperText, List, ListItem, Radio, Stack, Switch, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ModalActions } from '../../../components/CustomModal';
import { useCreateDashboardMutation } from '@fiji/common/src/features/dashboardManagement/dashboardApi';
import { useDispatch } from 'react-redux';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { useNavigate, useParams } from 'react-router-dom';
import { useTypedSelector } from '@fiji/common/src/app/store';
import { useGetDeviceByIdQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { usePayloadActions } from '@fiji/common/src/hooks/usePayloadActions';
import { handleWhiteSpaces } from 'utils/helpers';
import { useRBAC, useTextFieldError } from 'hooks';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useTranslation } from 'react-i18next';
import { useSetDashboard } from '../Common';
import LanguageTextField from 'components/FormElements/LanguageTextField';

type DashboardProps = {
    header?: string;
    actionButtonName?: any;
    [key: string]: any;
};

// ---------------------------------------------------------------------------
// Pure helpers – each < 10 CCN, no component dependencies
// ---------------------------------------------------------------------------

/**
 * Determines the final assignType for dashboard creation/cloning.
 * A default-source action always targets SOURCE; a device page with a SOURCE
 * selection is redirected to DEVICE; otherwise the caller's value is used.
 * CCN 3
 */
function resolveAssignType(
    isDefaultSource: boolean,
    isDeviceSource: boolean,
    dataAssignType: string | undefined
): string | undefined {
    if (isDefaultSource) return 'SOURCE';
    if (isDeviceSource) return 'DEVICE';
    return dataAssignType;
}

/**
 * Builds the assignTypeIds array that corresponds to the resolved assignType.
 * CCN 4
 */
function resolveAssignTypeIds(
    isDefaultSource: boolean,
    isDeviceSource: boolean,
    sourceAssignId: string | undefined,
    propDeviceId: string | undefined,
    dataAssignTypeIds: string[] | undefined
): string[] {
    if (isDefaultSource) return [sourceAssignId].filter(Boolean) as string[];
    if (isDeviceSource) return [propDeviceId].filter(Boolean) as string[];
    return dataAssignTypeIds ?? [];
}

// ---------------------------------------------------------------------------

export const CreateDashboardModal = React.forwardRef((props: DashboardProps, ref): JSX.Element => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const theme: any = useTheme();
    const { deviceId, dashboardId } = useParams();
    const navigate = useNavigate();
    const [cleanPayload] = usePayloadActions();
    const [payload, setPayload] = React.useState({
        name: '',
        description: '',
        isSystemDashboard: true,
        defaultSource: false,
        newDashboardDefault: false,
    });
    const { data: deviceData }: any = useGetDeviceByIdQuery(deviceId, { skip: !deviceId });
    const currentRealmName = useTypedSelector((rootstate) => rootstate.common.selectedRealm);
    const [errorMsg, setErrorMsg] = React.useState<any>(null);
    const [
        createDashboard,
        { isSuccess: dashboardCreationSuccess, data: dashboardData, isLoading, error: errorMessageObj },
    ]: any = useCreateDashboardMutation();
    const permissions = useTypedSelector(selectCurrentPermission);

    const { hasPermission } = useRBAC(permissions);
    const { hasTextError, makeErrorHandler } = useTextFieldError();
    const canCreateUserDashboard = hasPermission('dashboard-management_create-user-dashboards');
    const canCreateSystemDashboard = hasPermission('dashboard-management_create-system-dashboards');
    const { handleSubmit } = useSetDashboard();

    // Stable derivations – computed once per render to eliminate repeated optional chains.
    const name = payload.name;
    const description = payload.description;
    const deviceTypeName = deviceData?.data?.deviceTypeName;
    const buttonTransKey = props?.actionButtonName?.props?.translationKey;

    const handleDashboardCreation = async (data: any): Promise<void> => {
        const payloadClone = JSON.parse(JSON.stringify(payload));
        delete payloadClone['defaultSource'];
        if (payloadClone['id']) {
            payloadClone['referenceDashboardId'] = payloadClone['id'];
            delete payloadClone['status'];
            delete payloadClone['id'];
            delete payloadClone['createdAt'];
            delete payloadClone['createdBy'];
            delete payloadClone['updatedAt'];
            delete payloadClone['updatedBy'];
            delete payloadClone['widgetIdList'];
            delete payloadClone['widgetLayout'];
            delete payloadClone['associatedCount'];
            delete payloadClone['sourceId'];
            delete payloadClone['sourceName'];
            delete payloadClone['assignment'];
        }

        const isDefaultSource = payload.defaultSource || payload.newDashboardDefault;
        const isDeviceSource = Boolean(props?.deviceId) && data?.assignType === 'SOURCE';
        const sourceAssignId = deviceData?.data?.deviceTypeId ?? data?.sourceId;

        const assignType = resolveAssignType(isDefaultSource, isDeviceSource, data?.assignType);
        const assignTypeIds = resolveAssignTypeIds(
            isDefaultSource,
            isDeviceSource,
            sourceAssignId,
            props?.deviceId,
            data?.assignTypeIds
        );
        if (!assignType) return;

        const toastSuffix =
            buttonTransKey === 'DASHBOARDS:CLONE_DASHBOARD'
                ? t('DASHBOARDS:CLONE_MODAL_DASHBOARD.HAS_BEEN_CREATED')
                : t('DASHBOARDS:CLONE_MODAL_DASHBOARD.HAS_BEEN_SAVED');

        const { error }: any = await createDashboard({
            ...cleanPayload(payloadClone),
            assignType,
            assignTypeIds,
        });
        if (!error) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: `${name}${toastSuffix}`,
                    duration: 3000,
                })
            );
            onCancel();
        }
    };
    const { data, onClick, onCancel, isVisible, onConfirm } = useConfirm(handleDashboardCreation as any);

    React.useEffect(() => {
        if (errorMessageObj?.data?.errorMessage) {
            setErrorMsg(errorMessageObj?.data?.errorMessage?.[0]);
        }
    }, [errorMessageObj]);

    React.useEffect(() => {
        if (canCreateUserDashboard && !canCreateSystemDashboard) {
            setPayload((prev: any) => ({ ...prev, isSystemDashboard: false }));
        }
    }, [canCreateSystemDashboard, canCreateUserDashboard]);

    React.useEffect(() => {
        if (dashboardCreationSuccess && dashboardData && !data?.id) {
            navigate(`/${currentRealmName}/editDashboard/${data?.assignTypeIds[0]}/${dashboardData?.data?.id}`, {
                state: {
                    assignType: dashboardData?.data?.assignType,
                    assignId:
                        dashboardData?.data?.assignType === 'SOURCE'
                            ? data?.assignTypeIds[0]
                            : dashboardData?.data?.assignTypeIds[0],
                },
            });
        }
    }, [dashboardCreationSuccess, dashboardData]);

    React.useEffect(() => {
        if (dashboardId && dashboardCreationSuccess && dashboardData) {
            void handleSubmit(dashboardData?.data);
        }
    }, [dashboardCreationSuccess, dashboardData]);

    useEffect(() => {
        if (data?.id) {
            setPayload((prev: any) => ({
                ...prev,
                ...data,
                name: `${data?.name}-copy`,
                newDashboardDefault: data?.newDashboardDefault ?? false,
            }));
        }
    }, [data]);
    useImperativeHandle(
        ref,
        () => ({
            handleModalAction: (type: boolean, selectedData: any) => handleModalAction(type, selectedData),
        }),
        []
    );

    const handleModalAction = (type: boolean, selectedData?: any): void => {
        if (type) {
            if (selectedData?.assignType === 'SOURCE') {
                setPayload((prev: any) => ({ ...prev, defaultSource: true }));
            }
            onClick(selectedData);
            return;
        }
        onCancel();
    };
    const isSaveDisabled = isLoading || !name || !handleWhiteSpaces(name) || !data?.assignType || hasTextError;

    const createDashboardActions: ModalActions[] = [
        {
            key: 'cancel',
            label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
            close: true,
            variant: 'outlined',
            handleClick: (): void => {
                onCancel();
                setErrorMsg(null);
            },
        },
        {
            key: 'save',
            label: props?.actionButtonName ?? <CustomTransComponent translationKey={'COMMON:SAVE_LABEL'} />,
            variant: 'contained',
            isLoading: isLoading,
            disabled: isSaveDisabled,
            handleClick: onConfirm,
        },
    ];
    const handlePayloadChange = (type: any, value: any): void => {
        setPayload((prev: any) => ({ ...prev, [type]: value }));
    };

    const handleKeyDown = (event: any): any => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };

    return (
        <CustomModal
            isOpen={isVisible}
            isLoading={false}
            type="primary"
            actions={createDashboardActions}
            header={props?.header ?? <CustomTransComponent translationKey={'DASHBOARDS:CREATE_DASHBOARD'} />}
            data-testid={props?.['data-testid'] ?? 'custom-modal'}
        >
            <Stack py={2}>
                <Typography variant="body1" fontWeight={'600'} mb={2} className="black-500">
                    <CustomTransComponent translationKey={'DASHBOARDS:CLONE_MODAL_DASHBOARD.DESCRIPTION'} />
                </Typography>
                <LanguageTextField
                    fullWidth
                    name="name"
                    label={<CustomTransComponent translationKey={'COMMON:NAME'} />}
                    value={name}
                    inputProps={{ maxLength: 32 }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                        handlePayloadChange(e.target.name, e.target.value);
                        if (errorMsg?.length) {
                            setErrorMsg(null);
                        }
                    }}
                    variant="filled"
                    className="margin-top-18"
                    required
                    id="outlined-required"
                    data-testid="clone-dashboard-name"
                    handleTextError={makeErrorHandler('name')}
                    {...(errorMsg && {
                        error: true,
                        helperText: errorMsg,
                    })}
                />
                <Stack direction={'row'} justifyContent={'space-between'} px={2}>
                    <FormHelperText>
                        <CustomTransComponent translationKey={'COMMON:REQUIRED_LABEL'} />
                    </FormHelperText>
                    <FormHelperText>{`${name.length}/32`}</FormHelperText>
                </Stack>
                <LanguageTextField
                    id="outlined-password-input"
                    data-testid="clone-dashboard-description"
                    fullWidth
                    value={description}
                    inputProps={{ maxLength: 256 }}
                    onChange={(e: any): void => handlePayloadChange(e.target.name, e.target.value)}
                    name="description"
                    className="margin-top-18"
                    variant="filled"
                    multiline
                    onKeyDown={handleKeyDown}
                    label={<CustomTransComponent translationKey={'COMMON:DESCRIPTION'} />}
                    rows={4}
                    handleTextError={makeErrorHandler('description')}
                />
                <FormHelperText className="text-align-end padding-x-16">{`${description.length}/256`}</FormHelperText>
                {canCreateSystemDashboard && canCreateUserDashboard && (
                    <>
                        <Typography variant="body1" fontWeight={'600'} className="black-500">
                            <CustomTransComponent
                                translationKey={'DASHBOARDS:CLONE_MODAL_DASHBOARD.SYSTEM_USER_DESCRIPTION'}
                            />
                        </Typography>
                        <List className="margin-top-18">
                            <ListItem disablePadding>
                                <FormControlLabel
                                    control={
                                        <Radio
                                            data-testid="create-dashboard-system-radio"
                                            checked={payload.isSystemDashboard}
                                            onChange={(): void => handlePayloadChange('isSystemDashboard', true)}
                                        />
                                    }
                                    label={
                                        <Typography variant="body1" className="black-500">
                                            <b>
                                                {' '}
                                                <CustomTransComponent
                                                    translationKey={'DASHBOARDS:CLONE_MODAL_DASHBOARD.SYSTEM_DASHBOARD'}
                                                />
                                            </b>{' '}
                                            <CustomTransComponent
                                                translationKey={
                                                    'DASHBOARDS:CLONE_MODAL_DASHBOARD.SYSTEM_DASHBOARD_DESCRIPTION'
                                                }
                                            />
                                        </Typography>
                                    }
                                    className="f-14 fw-400"
                                />
                            </ListItem>
                            <ListItem disablePadding>
                                <FormControlLabel
                                    className="f-14 fw-400"
                                    control={
                                        <Radio
                                            data-testid="create-dashboard-user-radio"
                                            checked={!payload.isSystemDashboard}
                                            onChange={(): void => handlePayloadChange('isSystemDashboard', false)}
                                        />
                                    }
                                    label={
                                        <Typography variant="body1" className="black-500">
                                            <b>
                                                {' '}
                                                <CustomTransComponent
                                                    translationKey={'DASHBOARDS:CLONE_MODAL_DASHBOARD.USER_DASHBOARD'}
                                                />
                                            </b>{' '}
                                            <CustomTransComponent
                                                translationKey={
                                                    'DASHBOARDS:CLONE_MODAL_DASHBOARD.USER_DASHBOARD_DESCRIPTION'
                                                }
                                            />
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        </List>
                    </>
                )}
                {(deviceTypeName || data?.assignType !== 'GROUP') && (
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                        <Typography variant="subtitle2" fontSize={'14px'} fontWeight={'600'} className="black-500">
                            {!canCreateSystemDashboard ? (
                                `Set as your personal default dashboard for ${deviceTypeName ?? data?.sourceName} `
                            ) : (
                                <CustomTransComponent
                                    translationKey={'DASHBOARDS:CLONE_MODAL_DASHBOARD.SET_AS_DEFAULT'}
                                    replace={{ deviceType: deviceTypeName ?? data?.sourceName }}
                                />
                            )}
                        </Typography>
                        <Switch
                            data-testid="create-dashboard-default-switch"
                            checked={payload.newDashboardDefault}
                            onChange={(): void => {
                                setPayload((prev: any) => ({
                                    ...prev,
                                    newDashboardDefault: !prev.newDashboardDefault,
                                }));
                            }}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: theme?.palette?.primary?.main,
                                    '& + .MuiSwitch-track': {
                                        backgroundColor: theme?.palette?.primary?.main,
                                    },
                                },
                                '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled': {
                                    color: theme?.palette?.primary?.[50],
                                    '& + .MuiSwitch-track': {
                                        backgroundColor: theme?.palette?.primary?.[200],
                                    },
                                },
                            }}
                        />
                    </Stack>
                )}
            </Stack>
        </CustomModal>
    );
});
